import { useState } from "react";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";
import { Search, PackageCheck } from "lucide-react";

const API = "/api";

const STATUS_COPY = {
  initiated: "Order created — awaiting payment",
  paid: "Paid — queued for delivery",
  processing: "Delivery in progress",
  complete: "Delivered in full",
  delayed: "Paced — delivery resumes automatically",
  failed: "Payment failed",
  expired: "Checkout expired",
  refunded: "Refunded",
};

const inputCls =
  "w-full border border-[#1F1F1F] bg-[#0D0D0D] px-4 py-3.5 text-sm text-white placeholder-[#555555] outline-none transition-colors duration-300 focus:border-[#E11D2E]";

export default function TrackPage() {
  const [orderNumber, setOrderNumber] = useState("");
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setResult(null);
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/orders/lookup`, { order_number: orderNumber, email });
      setResult(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Lookup failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-[#050505] pt-16" data-testid="track-page">
        <div className="mx-auto max-w-xl px-6 py-20 lg:py-28">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#E11D2E]">Track order</p>
            <h1 className="mt-4 font-display text-3xl uppercase leading-tight tracking-tight text-white sm:text-4xl">
              Where's my order?
            </h1>
            <p className="mt-3 text-sm text-[#8A8A8A]">
              Enter your order number (from your receipt email) and the email you ordered with.
            </p>

            <form data-testid="track-form" onSubmit={submit} className="mt-8 space-y-5">
              <input
                data-testid="track-order-number"
                required
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                placeholder="CS-2026-XXXXXX"
                className={inputCls}
              />
              <input
                data-testid="track-email"
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@brand.com"
                className={inputCls}
              />
              <button
                data-testid="track-submit"
                type="submit"
                disabled={busy}
                className="flex w-full items-center justify-center gap-2 bg-[#E11D2E] py-4 font-mono text-xs font-600 uppercase tracking-[0.14em] text-white transition-all duration-300 hover:bg-[#F13B47] hover:shadow-[0_0_32px_rgba(225,29,46,0.4)] disabled:opacity-50"
              >
                <Search size={14} /> {busy ? "Checking…" : "Check status"}
              </button>
            </form>

            {error && (
              <p data-testid="track-error" className="mt-6 border border-[#E11D2E]/40 bg-[#0D0D0D] p-4 font-mono text-xs text-[#E11D2E]">
                {error}
              </p>
            )}

            {result && (
              <div data-testid="track-result" className="mt-8 border border-[#1F1F1F] bg-[#0D0D0D] p-6">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-sm font-600 text-white">{result.order_number}</span>
                  <PackageCheck size={18} className="text-[#E11D2E]" />
                </div>
                <p className="mt-3 text-sm text-white">{result.platform} — {result.service_name}</p>
                {result.total_units && (
                  <p className="mt-1 font-mono text-xs text-[#8A8A8A]">
                    {result.total_units.toLocaleString("en-US")} {result.unit_label}
                  </p>
                )}
                <p className="mt-4 border border-[#E11D2E]/40 px-3 py-2 font-mono text-xs uppercase tracking-[0.14em] text-[#E11D2E]" data-testid="track-status">
                  {STATUS_COPY[result.status] || result.status}
                </p>
                <p className="mt-4 font-mono text-[11px] text-[#555555]">
                  Ordered {new Date(result.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  {" · "}Last update {new Date(result.updated_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </p>
              </div>
            )}
          </Reveal>
        </div>
      </div>
      <Footer />
    </>
  );
}
